/**
 * Shared helpers for resolving human-readable entity labels in chart AI flows.
 */

import {isLocalEntityRef, looksLikeUuid} from '../../../utils/entityLinks';

const BARE_QID_RE = /^Q\d+$/i;
const FALLBACK_LABEL = 'Unnamed entity';

/**
 * True when a string is only a machine id (Q-id / D-id / UUID), not a display name.
 * @param {string} value
 * @param {string} [id]
 * @returns {boolean}
 */
export function isMachineIdLabel(value, id) {
    if (!value || typeof value !== 'string') return true;
    const trimmed = value.trim();
    if (!trimmed) return true;
    if (BARE_QID_RE.test(trimmed) || isLocalEntityRef(trimmed) || looksLikeUuid(trimmed)) {
        return true;
    }
    if (id && trimmed.toUpperCase() === String(id).trim().toUpperCase()) return true;
    return false;
}

/**
 * Pull a display label from common Wikidata / search / plan shapes.
 * Skips bare QIDs so nested `labels.en` can win over SPARQL's `label: qid` fallback.
 * @param {object|null|undefined} entity
 * @returns {string|null}
 */
export function extractHumanLabelCandidate(entity) {
    if (!entity || typeof entity !== 'object') return null;
    const id = entity.wikidataId || entity.id || '';
    const nestedEn = entity.labels?.en;
    const nestedValue = typeof nestedEn === 'string'
        ? nestedEn
        : (nestedEn?.value || nestedEn?.label || null);
    const candidates = [
        entity.label,
        entity.canonicalName,
        entity.name,
        entity.title,
        nestedValue,
    ];
    for (const candidate of candidates) {
        if (candidate == null) continue;
        const trimmed = String(candidate).trim();
        if (!trimmed) continue;
        if (isBareQidLabel(trimmed, id)) continue;
        return trimmed;
    }
    return null;
}

/**
 * Prefer a human label over a bare QID / UUID when both are available.
 * Never returns a raw UUID or bare Q/D id as the card title.
 * @param {{label?: string, canonicalName?: string, name?: string, title?: string, labels?: object, id?: string, wikidataId?: string}} entity
 * @returns {string}
 */
export function resolveMutationEntityLabel(entity) {
    const id = entity?.wikidataId || entity?.id || '';
    const idNorm = String(id).trim();
    // Prefer KB / Wikidata canonical names over plan aliases or report nicknames.
    const candidates = [
        entity?.canonicalName,
        entity?.label,
        entity?.name,
        entity?.title,
        extractHumanLabelCandidate(entity),
    ];
    for (const candidate of candidates) {
        if (!candidate || typeof candidate !== 'string') continue;
        const trimmed = candidate.trim();
        if (!trimmed) continue;
        if (isMachineIdLabel(trimmed, idNorm)) continue;
        return trimmed;
    }
    return FALLBACK_LABEL;
}

export function isBareQidLabel(label, id) {
    return isMachineIdLabel(label, id);
}

/**
 * Strip non-relation phrases the model often puts on arrows ("connected to", "connect to").
 * Keep explicit relations like "friend", "at war", "ally".
 * @param {string|null|undefined} label
 * @returns {string|null}
 */
export function sanitizeArrowRelationLabel(label) {
    if (label == null) return null;
    const trimmed = String(label).trim();
    if (!trimmed) return null;
    if (/^(connect(ed)?(\s+to)?|link(ed)?(\s+to)?|related(\s+to)?|arrow)$/i.test(trimmed)) {
        return null;
    }
    // "connect X to Y" whole phrase leaked into label
    if (/^connect(ed)?\b.+\bto\b/i.test(trimmed) && !/\bas\b/i.test(trimmed) && !/\bat\s+war\b/i.test(trimmed)) {
        return null;
    }
    return trimmed;
}

/**
 * Merge plan/caller entity with Wikidata fetch without letting a QID fallback wipe a good label.
 * @param {object} entity
 * @param {object|null|undefined} fullEntity
 * @returns {object}
 */
export function mergeEntityForCard(entity, fullEntity) {
    const entityId = entity?.id || entity?.wikidataId || fullEntity?.id || fullEntity?.wikidataId || '';
    const planLabel = extractHumanLabelCandidate(entity);
    const fetchedLabel = extractHumanLabelCandidate(fullEntity);

    const merged = {
        ...entity,
        ...(fullEntity || {}),
    };

    const canonical = fullEntity?.canonicalName || fetchedLabel;
    if (canonical && !isBareQidLabel(canonical, entityId)) {
        merged.canonicalName = canonical;
        merged.label = canonical;
    } else if (!isBareQidLabel(fetchedLabel, entityId)) {
        merged.label = fetchedLabel;
    } else if (!isBareQidLabel(planLabel, entityId)) {
        merged.label = planLabel;
    } else {
        merged.label = resolveMutationEntityLabel({
            id: entityId,
            wikidataId: entityId,
            label: planLabel || fetchedLabel,
            canonicalName: fullEntity?.canonicalName || entity?.canonicalName,
            labels: fullEntity?.labels || entity?.labels,
            name: entity?.name || fullEntity?.name,
        });
    }
    return merged;
}
